/**
 * The shared "inherits unless overwritten" glance for a single per-tenant
 * cap (D1, plan-modal Task 5) — the field Task 7 uses to render every
 * ceiling on the tenant Plan tab. It lives in `@papan/core-ui`, not in
 * either feature package, because both `features/billing` (price book
 * presets) and `features/orgs` (the tenant console) need it and neither may
 * import the other — the same reason `CapInput` moved here.
 *
 * ── The glance convention (matches the existing code exactly) ───────────
 *
 * The inherited-field pattern already in the codebase (`CatalogAdmin.tsx`'s
 * `PlanRateLimits`, same `inheritedFieldState`) uses NO chip: inheriting
 * renders as a greyed placeholder, overridden renders as a filled input plus
 * a "Reset to <target> <value>" button. The reset button's PRESENCE is the
 * entire "overwritten" signal — do not invent a "Linked"/"Overwritten" badge
 * on top of it.
 */
import { useState } from 'react'
import { inheritedFieldState } from '@papan/types'
import { CapInput } from './CapInput'

/**
 * Minimal translator shape — mirrors the `T` alias `CatalogAdmin.tsx` already
 * uses for the same purpose, rather than importing `i18next`'s `TFunction`
 * directly (this package has no direct `i18next` dependency; `@papan/i18n`
 * pulls it in transitively).
 */
export type InheritedCapFieldTranslator = (key: string, opts?: Record<string, unknown>) => string

export interface InheritedCapFieldProps {
  label: string
  hint?: string
  /**
   * The OVERRIDE record value, or `null` when inheriting.
   *
   * Known limit (not reachable via this control): `saved: number | null`
   * cannot distinguish an explicit `unlimited` override (`value: null`) from
   * `inherit` — both read as inheriting here. Free/extra caps CAN carry an
   * `unlimited` row via SQL/repair, which would glance-render as inheriting.
   * Grace has no `unlimited` state, so grace is exempt.
   */
  saved: number | null
  /** The plan/derived value; `null` = unlimited/absent baseline. */
  inherited: number | null
  /** Placeholder shown when `inherited` is `null` — e.g. "Unlimited" /
   *  "Plan default". Never coerced to a number. */
  nullMeans: string
  /** `null` = clear the override (return to inheriting). */
  onCommit: (next: number | null) => void
  /** The NOUN only — "plan" / "platform default" — interpolated into the
   *  reset button's label. */
  resetTarget: string
  /** Caller-supplied formatter. Money axes must bind the billing currency —
   *  never a hardcoded `/100`. */
  format: (v: number) => string
  scale?: 'money' | 'int'
  zeroIsBlock?: boolean
  disabled?: boolean
  ariaLabel?: string
  t: InheritedCapFieldTranslator
  /** #897 — names the box's typed-but-uncommitted text so it survives a
   *  phone/desktop layout swap (forwarded to `CapInput`). Absent = local state. */
  draftKey?: string
}

export function InheritedCapField({
  label,
  hint,
  saved,
  inherited,
  nullMeans,
  onCommit,
  resetTarget,
  format,
  scale = 'money',
  zeroIsBlock,
  disabled,
  ariaLabel,
  t,
  draftKey,
}: InheritedCapFieldProps) {
  const st = inheritedFieldState<number | null>(saved, inherited, Object.is)
  // Bumped on every hand-off: a commit that leaves `saved` as it was (re-pinning
  // the value already pinned) must still give a fresh box — see the key below.
  const [handoffs, setHandoffs] = useState(0)
  const commit = (next: number | null) => {
    setHandoffs(n => n + 1)
    onCommit(next)
  }
  // The box is re-created whenever `saved` changes (see its `key`), dropping
  // its typed text; its draft key follows `saved` too, so a re-created box
  // never picks up the old box's draft. Not keyed on `handoffs`: a hand-off
  // commits, and a commit already clears the box's draft — and `handoffs`
  // restarts at 0 on the other shell, which would orphan a swapped draft.
  const boxDraftKey = draftKey ? `${draftKey}:${saved == null ? 'inherit' : `saved:${saved}`}` : undefined
  const placeholderText = st.placeholder == null ? nullMeans : format(st.placeholder)
  // `resetTo` mirrors `placeholder`'s nullability: when the field inherits an
  // unlimited baseline, resetting returns to `null`, not a formattable number.
  const resetValueText = st.resetTo == null ? nullMeans : format(st.resetTo)
  const resetLabel = t('settings.platform.customer.inherited.reset', {
    defaultValue: 'Reset to {{target}} {{value}}',
    target: resetTarget,
    value: resetValueText,
  })

  return (
    <div className="flex items-center gap-1.5" title={hint}>
      <span className="text-xs themed-text-muted whitespace-nowrap">{label}</span>
      <CapInput
        // A NEW box whenever the page's value changes (a value staged, Discard,
        // Save) and on every hand-off. `st.value` hides a pin equal to the
        // inherited value (drawn blank), so a change between "inherit" and that
        // pin — or re-pinning it — never reaches the box as a new `value`:
        // without this its typed text outlived the page's draft (still shown
        // after Discard, and clearing it never reached the page).
        //
        // Accepted: a window blur mid-edit (switching apps) commits, so the box
        // is re-created and the caret is not restored on return — nothing is
        // lost (the value is staged).
        key={`${saved == null ? 'inherit' : `saved:${saved}`}:${handoffs}`}
        value={st.value}
        onCommit={commit}
        scale={scale}
        nullLabel={placeholderText}
        // No chip (D1): CapInput's own blank-state chip would duplicate the
        // placeholder on every inheriting field. `zeroIsBlock`'s red Blocked
        // chip is a separate, intentional affordance and is NOT suppressed.
        hideNullChip
        zeroIsBlock={zeroIsBlock}
        disabled={disabled}
        ariaLabel={ariaLabel ?? label}
        draftKey={boxDraftKey}
      />
      {st.overridden && (
        <button
          type="button"
          disabled={disabled}
          // Reset = return to inheriting (clear the override).
          // Keep focus in the box on press: else its blur commits what was typed
          // first, and typing the inherited value turns `overridden` off and
          // unmounts this button before the click lands — Save would then write
          // a hidden pin instead of inheriting. The click re-keys the box (to
          // `inherit`), dropping the typed text.
          onMouseDown={e => e.preventDefault()}
          onClick={() => onCommit(null)}
          title={resetLabel}
          className="text-[10px] themed-text-muted hover:themed-text underline underline-offset-2 disabled:opacity-50 whitespace-nowrap"
        >
          {resetLabel}
        </button>
      )}
    </div>
  )
}
